import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ArrowRight,
  CheckCircle2,
  DollarSign,
  Wallet,
  RotateCcw,
  Plus,
  Phone,
  Gift,
  Percent,
  Calendar,
} from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/formatters';
import { DriverPaySettlement } from '@/types/financial.types';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { SettlementTypeBadge } from '@/components/features/financials/shared/SettlementTypeBadge';
import { AddPayAdjustmentModal } from '@/components/features/financials/AddPayAdjustmentModal';
import { useI18n } from '@/contexts/I18nContext';

const STATUS_COLOR: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground border-border',
  confirmed: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  paid: 'bg-green-500/10 text-green-600 border-green-500/20',
  reversed: 'bg-red-500/10 text-red-600 border-red-500/20',
};

const ITEM_TYPE_COLOR: Record<string, string> = {
  accrual: 'bg-blue-50 text-blue-700 border-blue-200',
  salary: 'bg-purple-50 text-purple-700 border-purple-200',
  bonus: 'bg-green-50 text-green-700 border-green-200',
  penalty: 'bg-red-50 text-red-700 border-red-200',
  adjustment: 'bg-orange-50 text-orange-700 border-orange-200',
};

export default function DriverPaySettlementDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { get } = useApi();
  const { patch, loading } = useApi({ showSuccessToast: false, showErrorToast: false });
  const [settlement, setSettlement] = useState<DriverPaySettlement | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);

  const STATUS_LABEL: Record<string, string> = {
    draft: t.financials.status_draft,
    confirmed: t.financials.status_confirmed,
    paid: t.financials.status_paid,
    reversed: t.financials.status_reversed,
  };

  const ITEM_TYPE_LABEL: Record<string, string> = {
    accrual: t.financials.accrual,
    salary: t.financials.salary,
    bonus: t.financials.bonus,
    penalty: t.financials.penalty,
    adjustment: t.financials.adjustment,
  };

  useEffect(() => {
    if (id) fetchSettlement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchSettlement = async () => {
    setHasFetched(false);
    try {
      const result: any = await get(`/api/v1/driver-pay-settlements/${id}`);
      const data = result?.success !== undefined ? result?.data : result;
      setSettlement(data);
    } catch {
      toast.error('فشل في تحميل تفاصيل التسوية');
    } finally {
      setHasFetched(true);
    }
  };

  const handleConfirm = async () => {
    try {
      await patch(`/api/v1/driver-pay-settlements/${id}/confirm`, {});
      toast.success(t.financials.settlement_confirmed);
      fetchSettlement();
    } catch (err: any) {
      toast.error(err?.message ?? 'فشل في تأكيد التسوية');
    }
  };

  const handleMarkPaid = async () => {
    const paid_via = window.prompt(t.financials.paid_via, 'bank_transfer');
    if (!paid_via) return;
    try {
      await patch(`/api/v1/driver-pay-settlements/${id}/pay`, { paid_via });
      toast.success(t.financials.settlement_paid);
      fetchSettlement();
    } catch (err: any) {
      toast.error(err?.message ?? 'فشل في تحديث حالة التسوية');
    }
  };

  const handleReverse = async () => {
    try {
      await patch(`/api/v1/driver-pay-settlements/${id}/reverse`, {});
      toast.success(t.financials.settlement_reversed);
      fetchSettlement();
    } catch (err: any) {
      toast.error(err?.message ?? 'فشل في إلغاء التسوية');
    }
  };

  if (!hasFetched) return <LoadingSkeleton type="card" rows={4} />;
  if (!settlement) return null;

  const items = settlement.items ?? [];
  const totalAmount = items.reduce((s, r) => s + Number(r.amount), 0);

  return (
    <PageContainer>
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
              تفاصيل التسوية
              <SettlementTypeBadge type="pay" />
            </h1>
            <div className="text-sm text-muted-foreground mt-0.5 flex flex-wrap items-center gap-3">
              <span>السائق: {settlement.driver_name}</span>
              {settlement.driver_phone && (
                <a
                  href={`tel:${settlement.driver_phone}`}
                  className="flex items-center gap-1 text-primary hover:underline"
                  dir="ltr"
                >
                  <Phone className="h-3 w-3" />
                  {settlement.driver_phone}
                </a>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatDate(settlement.period_start)} — {formatDate(settlement.period_end)}
              </span>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/dashboard/driver-pay-settlements')}
            className="gap-1 self-start sm:self-auto"
          >
            <ArrowRight className="h-4 w-4" />
            رجوع
          </Button>
        </div>
      </div>

      <div className="space-y-6" dir="rtl">
        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <span className="text-xs text-muted-foreground">{t.financials.trips_count}</span>
              <div className="text-xl font-bold mt-1">{settlement.trips_count}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <span className="text-xs text-muted-foreground">{t.financials.base_amount}</span>
              <div className="text-xl font-bold mt-1">{formatCurrency(settlement.base_amount)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <span className="text-xs text-muted-foreground">{t.financials.bonus_amount}</span>
              <div className="text-xl font-bold mt-1 text-green-600">
                {formatCurrency(settlement.bonus_amount)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <span className="text-xs text-muted-foreground">{t.financials.total_amount}</span>
              <div className="text-xl font-bold mt-1 text-primary">
                {formatCurrency(settlement.total_amount)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Snapshot block */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">صورة الإعدادات وقت الإنشاء</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Wallet className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-xs text-muted-foreground">{t.financials.payment_type}</div>
                  <div className="font-medium">
                    {settlement.payment_type_snapshot === 'salary'
                      ? t.financials.salary_type
                      : t.financials.commission_type}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Percent className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-xs text-muted-foreground">{t.financials.commission_rate}</div>
                  <div className="font-medium">
                    {settlement.commission_rate_snapshot != null
                      ? `${(Number(settlement.commission_rate_snapshot) * 100).toFixed(0)}%`
                      : '—'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Gift className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-xs text-muted-foreground">{t.financials.per_trip_bonus}</div>
                  <div className="font-medium">
                    {settlement.per_trip_bonus_snapshot != null
                      ? formatCurrency(Number(settlement.per_trip_bonus_snapshot))
                      : '—'}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info card + actions */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">معلومات التسوية</CardTitle>
              <div className="flex items-center gap-2">
                <SettlementTypeBadge type="pay" />
                <Badge className={cn('border text-xs', STATUS_COLOR[settlement.status])}>
                  {STATUS_LABEL[settlement.status] ?? settlement.status}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">تاريخ الإنشاء:</span>
                <span className="me-2">{formatDate(settlement.created_at)}</span>
              </div>
              {settlement.confirmed_at && (
                <div>
                  <span className="text-muted-foreground">تاريخ التأكيد:</span>
                  <span className="me-2">{formatDate(settlement.confirmed_at)}</span>
                </div>
              )}
              {settlement.paid_at && (
                <div>
                  <span className="text-muted-foreground">تاريخ الدفع:</span>
                  <span className="me-2">{formatDate(settlement.paid_at)}</span>
                </div>
              )}
              {settlement.paid_via && (
                <div>
                  <span className="text-muted-foreground">{t.financials.paid_via}:</span>
                  <span className="me-2">{settlement.paid_via}</span>
                </div>
              )}
              {settlement.notes && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">ملاحظات:</span>
                  <span className="me-2">{settlement.notes}</span>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              {settlement.status === 'draft' && (
                <>
                  <Button
                    onClick={() => setShowAdjustmentModal(true)}
                    disabled={loading}
                    variant="outline"
                    className="gap-1.5"
                  >
                    <Plus className="h-4 w-4" />
                    {t.financials.add_adjustment}
                  </Button>
                  <Button onClick={handleConfirm} disabled={loading} className="gap-1.5">
                    <CheckCircle2 className="h-4 w-4" />
                    {t.financials.confirm_settlement}
                  </Button>
                  <Button
                    onClick={handleReverse}
                    disabled={loading}
                    variant="outline"
                    className="gap-1.5 border-red-500 text-red-600 hover:bg-red-50"
                  >
                    <RotateCcw className="h-4 w-4" />
                    {t.financials.reverse_settlement}
                  </Button>
                </>
              )}
              {settlement.status === 'confirmed' && (
                <>
                  <Button
                    onClick={handleMarkPaid}
                    disabled={loading}
                    className="gap-1.5 bg-green-600 hover:bg-green-700"
                  >
                    <DollarSign className="h-4 w-4" />
                    {t.financials.mark_paid}
                  </Button>
                  <Button
                    onClick={handleReverse}
                    disabled={loading}
                    variant="outline"
                    className="gap-1.5 border-red-500 text-red-600 hover:bg-red-50"
                  >
                    <RotateCcw className="h-4 w-4" />
                    {t.financials.reverse_settlement}
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Items table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">عناصر التسوية ({items.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {items.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">لا توجد عناصر</p>
            ) : (
              <div className="rounded-b-lg overflow-hidden">
                <Table dir="rtl">
                  <TableHeader>
                    <TableRow className="h-10 bg-muted/50">
                      <TableHead className="text-end text-xs font-semibold px-4">النوع</TableHead>
                      <TableHead className="text-end text-xs font-semibold px-4">رقم الرحلة</TableHead>
                      <TableHead className="text-end text-xs font-semibold px-4">رقم التتبع</TableHead>
                      <TableHead className="text-end text-xs font-semibold px-4">الوصف</TableHead>
                      <TableHead className="text-end text-xs font-semibold px-4">المبلغ</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={item.id} className="h-10">
                        <TableCell className="px-4 py-2">
                          <Badge
                            variant="outline"
                            className={cn('text-xs', ITEM_TYPE_COLOR[item.item_type])}
                          >
                            {ITEM_TYPE_LABEL[item.item_type] ?? item.item_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-2 font-mono text-xs text-muted-foreground">
                          {item.trip_number ?? '—'}
                          {item.trip_type ? <span className="ms-1 opacity-60">({item.trip_type})</span> : null}
                        </TableCell>
                        <TableCell className="px-4 py-2 font-mono text-xs">
                          {item.tracking_number ?? '—'}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-muted-foreground">
                          {item.description ?? '—'}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm font-bold">
                          {formatCurrency(item.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow className="bg-muted/60 font-semibold border-t-2">
                      <TableCell className="px-4 py-3 text-sm font-bold" colSpan={4}>
                        الإجمالي
                      </TableCell>
                      <TableCell className="px-4 py-3 text-sm font-bold">
                        {formatCurrency(totalAmount)}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {id && (
        <AddPayAdjustmentModal
          open={showAdjustmentModal}
          onOpenChange={setShowAdjustmentModal}
          settlementId={id}
          onSuccess={fetchSettlement}
        />
      )}
    </PageContainer>
  );
}
